import React from 'react';
import { Field, FieldProps } from 'formik';
import {
  parseIntervalFromString,
  formatIntervalForComputer,
} from '../../../../util/dates';
import { I18n } from '@lingui/react';
import { validateNumber } from 'util/validation';
import { IntervalUnit, INTERVAL_UNITS } from 'util/backendapi/types/Enum';
import SimpleSelect, { SimpleSelectOption } from '../simpleselect/simpleselect';

export type IntervalFieldValue = string | null;

type OwnProps = {
  name: string;
  id?: string;
  disabled?: boolean;
  defaultUnit?: IntervalUnit;
  unitOptions?: IntervalUnit[];
};

type Props = OwnProps & FieldProps;

interface State {
  intervalNumber: string;
  intervalUnit: IntervalUnit;
  intervalUnitOptions: SimpleSelectOption<IntervalUnit>[];
}

const DEFAULT_UNIT: IntervalUnit = 'days';

/**
 * A custom component designed to be used with Formik's <Input> component.
 *
 * This is made to be used as a <Field> within <Formik>. It takes and returns
 * a form value of type null | string, where the string is an ISO duration string.
 *
 * @example See `intervalfield.stories.js`
 * @export
 * @class IntervalField
 * @extends {React.Component}
 */
class IntervalFieldInner extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const { defaultUnit, unitOptions } = this.props;
    const { intervalNumber, intervalUnit } = parseIntervalFromString(
      this.props.field.value as IntervalFieldValue
    );

    const intervalUnitOptions = (unitOptions || INTERVAL_UNITS).map((unit) => ({
      label: unit,
      value: unit,
    }));

    this.state = {
      intervalNumber: intervalNumber === null ? '' : intervalNumber.toString(),
      intervalUnit: intervalUnit || defaultUnit || DEFAULT_UNIT,
      intervalUnitOptions: intervalUnitOptions,
    };
  }

  handleIntervalNumberChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    this.setState(
      { intervalNumber: event.target.value },
      this._updateFormikValues
    );
  };

  handleIntervalUnitChange = (selectedUnit: IntervalUnit | null) => {
    this.setState(
      { intervalUnit: selectedUnit || this.props.defaultUnit || DEFAULT_UNIT },
      this._updateFormikValues
    );
  };

  handleBlur = () => {
    this.props.form.setFieldTouched(this.props.field.name);
  };

  _updateFormikValues = () => {
    const { intervalNumber, intervalUnit } = this.state;

    const newValue: IntervalFieldValue =
      validateNumber(intervalNumber) && Number(intervalNumber) >= 0
        ? formatIntervalForComputer(Number(intervalNumber), intervalUnit)
        : null;
    this.props.form.setFieldValue(this.props.field.name, newValue);
    this.props.form.setFieldTouched(this.props.field.name, true);
  };

  render() {
    const { field } = this.props;
    return (
      <>
        <input
          id={`${this.props.id || field.name}-interval-number`}
          type="text"
          className="IntervalInput-number"
          name={`${field.name}-number`}
          onChange={this.handleIntervalNumberChange}
          onBlur={this.handleBlur}
          value={this.state.intervalNumber}
          disabled={Boolean(this.props.disabled)}
        />
        <I18n>
          {({ i18n }) => (
            <SimpleSelect
              id={`${this.props.id || field.name}-interval-unit`}
              name={`${field.name}-unit`}
              className="IntervalInput-unit"
              onChange={this.handleIntervalUnitChange}
              value={this.state.intervalUnit || ''}
              options={this.state.intervalUnitOptions}
              isDisabled={Boolean(this.props.disabled)}
            />
          )}
        </I18n>
      </>
    );
  }
}

export function IntervalField(props: OwnProps) {
  return <Field component={IntervalFieldInner} {...props} />;
}
IntervalField.WrappedComponent = IntervalFieldInner;
